import React from 'react';
import { Image, Link } from './common/NextAdapters';
import { Icon } from '@iconify/react';
import { Button } from './ui/button';
import { TitleMeta } from './title-meta';

/**
 * Cinematic featured hero for the Movies page.
 * Highlights the flagship film with immersive artwork and direct actions.
 */
export function MovieSpotlight({ movie }) {
  if (!movie) return null;

  return (
    <section className="relative isolate min-h-[65vh] sm:min-h-[72vh] lg:min-h-[78vh] flex items-end overflow-hidden">
      {movie.thumbnail ? (
        <Image
          src={movie.thumbnail}
          alt=""
          fill
          priority
          sizes="100vw"
          className="object-cover object-center"
        />
      ) : (
        <div className="absolute inset-0 bg-card" />
      )}

      <div className="absolute inset-0 bg-black/10" />
      <div className="absolute inset-0 bg-gradient-to-r from-background via-background/50 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-background via-background/50 to-transparent" />

      <div className="relative mx-auto w-full max-w-[1600px] gutter pt-28 pb-12 sm:pt-36 sm:pb-16 lg:pb-20">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-1 text-xs font-semibold tracking-wider text-white uppercase backdrop-blur-md">
            <span className="size-1.5 rounded-full bg-ember animate-pulse" />
            Spotlight Film
          </div>

          <h1 className="display mt-4 text-[clamp(2.5rem,6.5vw,5rem)] leading-[0.95] text-balance">
            {movie.title}
          </h1>

          <div className="mt-3">
            <TitleMeta
              className="text-xs sm:text-sm"
              items={[
                movie.category,
                movie.year,
                movie.director ? `Directed by ${movie.director}` : null,
              ]}
            />
          </div>

          {movie.description ? (
            <p className="mt-4 line-clamp-3 max-w-[48ch] text-[15px] leading-relaxed text-foreground/80 sm:text-base">
              {movie.description}
            </p>
          ) : null}

          {movie.cast && movie.cast.length > 0 ? (
            <p className="mt-3 text-xs sm:text-sm text-muted-foreground">
              Starring {movie.cast.slice(0, 3).join(', ')}
            </p>
          ) : null}

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Button
              asChild
              size="lg"
              className="h-12 rounded-full bg-white px-8 text-base font-semibold text-black transition-transform hover:scale-102 hover:bg-white/95 focus-visible:ring-3 focus-visible:ring-ring/50"
            >
              <Link href={`/watch/${movie.id}`}>
                <Icon icon="ph:play-fill" className="size-5" />
                Watch Movie
              </Link>
            </Button>

            {movie.trailer ? (
              <Button
                asChild
                variant="outline"
                size="lg"
                className="h-12 rounded-full border-white/25 bg-white/10 px-6 text-base font-medium backdrop-blur-md hover:bg-white/20 hover:text-white"
              >
                <Link href={`/trailer/${movie.id}`}>
                  <Icon icon="ph:film-reel" className="size-5" />
                  Trailer
                </Link>
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

export default MovieSpotlight;
